import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Card,
  Button,
  Avatar,
  useTheme,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Radio,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Tooltip,
  Alert,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SecurityIcon from "@mui/icons-material/Security";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectStrategies,
  selectManagers,
} from "../../../Redux/Reducers/selectors";
import { getUserState } from "../../../Redux/Reducers/userReducer";
import { setStrategies } from "../../../Redux/Reducers/strategiesSlice";
import {
  addActiveRequests,
  removeActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../Redux/Reducers/uiSlice";
import { updateStrategy } from "../../../utils/restAPI";
import {
  PERMISSION_LEVEL,
  PERMISSION_LEVEL_OPTIONS,
  normalizePermissions,
  buildPermissionsPatch,
  canEditStrategyPermissions,
} from "../permissionLevels";

const makeRowId = () =>
  `row_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const createEmptyRow = () => ({
  id: makeRowId(),
  kind: "user",
  key: "",
  level: PERMISSION_LEVEL.Read,
});

const permissionsToRows = (permissions) => {
  const perms = normalizePermissions(permissions);
  const userRows = Object.entries(perms.user)
    .filter(([, level]) => Number(level) !== PERMISSION_LEVEL.None)
    .map(([email, level]) => ({
      id: makeRowId(),
      kind: "user",
      key: email,
      level: Number(level),
    }));
  const managerRows = Object.entries(perms.manager)
    .filter(([, level]) => Number(level) !== PERMISSION_LEVEL.None)
    .map(([managerId, level]) => ({
      id: makeRowId(),
      kind: "manager",
      key: managerId,
      level: Number(level),
    }));
  return [...userRows, ...managerRows];
};

const rowsToPermissions = (rows, defaultLevel) => {
  const user = {};
  const manager = {};
  (rows || []).forEach((row) => {
    const key = String(row?.key || "").trim();
    if (!key) return;
    const level = Number(row.level);
    if (row.kind === "user") {
      if (!key.includes("@")) return;
      user[key.toLowerCase()] = level;
    } else if (row.kind === "manager") {
      manager[key] = level;
    }
  });
  return normalizePermissions({
    default: defaultLevel,
    user,
    manager,
  });
};

const LEVEL_COLUMNS = [
  { value: PERMISSION_LEVEL.List, label: "List" },
  { value: PERMISSION_LEVEL.Read, label: "Read" },
  { value: PERMISSION_LEVEL.ReadNarrow, label: "Narrow Read" },
  { value: PERMISSION_LEVEL.Write, label: "Write" },
];

const StrategyPermissionsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const strategyId = location.state?.strategyId
    ? String(location.state.strategyId)
    : null;

  const strategies = useSelector(selectStrategies);
  const managers = useSelector(selectManagers);
  const user = useSelector(getUserState);

  const strategy = useMemo(
    () =>
      (strategies || []).find(
        (s) => String(s?.strategyId || s?.id) === String(strategyId)
      ) || null,
    [strategies, strategyId]
  );

  const goBackToStrategies = () => {
    navigate("/strategies");
  };

  const canEdit = canEditStrategyPermissions(strategy, user, managers);

  const [baseline, setBaseline] = useState(() => normalizePermissions(null));
  const [defaultLevel, setDefaultLevel] = useState(PERMISSION_LEVEL.List);
  const [rows, setRows] = useState(() => [createEmptyRow()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!strategyId) {
      navigate("/strategies", { replace: true });
    }
  }, [strategyId, navigate]);

  useEffect(() => {
    if (!strategy) return;
    const perms = normalizePermissions(strategy.permissions);
    setBaseline(perms);
    setDefaultLevel(perms.default);
    const loaded = permissionsToRows(perms);
    setRows(loaded.length > 0 ? loaded : [createEmptyRow()]);
  }, [strategy]);

  const managerLabel = (managerId) => {
    const m = (managers || []).find((x) => x.managerId === managerId);
    if (!m) return managerId || "";
    return m.name || managerId;
  };

  const availableManagersForRow = (rowId) => {
    const assigned = new Set(
      rows
        .filter((r) => r.id !== rowId && r.kind === "manager" && r.key)
        .map((r) => r.key)
    );
    return (managers || []).filter(
      (m) => m?.managerId && !assigned.has(m.managerId)
    );
  };

  const updateRow = (rowId, patch) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, ...patch } : row))
    );
  };

  const handleKindChange = (rowId, nextKind) => {
    if (!nextKind) return;
    updateRow(rowId, { kind: nextKind, key: "" });
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleDeleteRow = (rowId) => {
    setRows((prev) => {
      const next = prev.filter((row) => row.id !== rowId);
      return next.length > 0 ? next : [createEmptyRow()];
    });
  };

  const handleSave = async () => {
    if (!strategy || !canEdit || saving) return;

    const filledRows = rows.filter((row) => {
      const key = String(row.key || "").trim();
      if (row.kind === "user") return key.includes("@");
      return Boolean(key);
    });

    const incomplete = rows.some((row) => {
      const key = String(row.key || "").trim();
      // Allow a single trailing empty row; flag partial/invalid rows
      if (!key) return false;
      if (row.kind === "user") return !key.includes("@");
      return false;
    });
    if (incomplete) {
      toast.error("Fix invalid emails before saving.");
      return;
    }

    const userKeys = filledRows
      .filter((r) => r.kind === "user")
      .map((r) => String(r.key).trim().toLowerCase());
    const managerKeys = filledRows
      .filter((r) => r.kind === "manager")
      .map((r) => String(r.key).trim());
    if (new Set(userKeys).size !== userKeys.length) {
      toast.error("Duplicate user emails are not allowed.");
      return;
    }
    if (new Set(managerKeys).size !== managerKeys.length) {
      toast.error("Duplicate managers are not allowed.");
      return;
    }

    const draft = rowsToPermissions(filledRows, defaultLevel);
    const permissionsPatch = buildPermissionsPatch(baseline, draft);
    if (!permissionsPatch) {
      toast.info("No permission changes to save");
      return;
    }

    try {
      setSaving(true);
      dispatch(addActiveRequests("modify_strategy"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Updating permissions..."));

      const resp = await updateStrategy(strategy.strategyId, {
        permissions: permissionsPatch,
      });
      if (resp?.error) throw new Error(resp.message || "Update failed");

      const nextPermissions =
        resp?.permissions ??
        resp?.strategy?.permissions ??
        resp?.data?.permissions ??
        draft;

      const updated = (strategies || []).map((s) =>
        s.strategyId === strategy.strategyId
          ? {
              ...s,
              permissions: nextPermissions,
              ...(resp?.date != null ? { date: resp.date } : {}),
            }
          : s
      );
      dispatch(setStrategies(updated));

      const normalized = normalizePermissions(nextPermissions);
      setBaseline(normalized);
      setDefaultLevel(normalized.default);
      const loaded = permissionsToRows(normalized);
      setRows(loaded.length > 0 ? loaded : [createEmptyRow()]);

      toast.success("Permissions updated");
      goBackToStrategies();
    } catch (error) {
      console.error("Failed to update permissions", error);
      toast.error(error?.message || "Failed to update permissions");
    } finally {
      setSaving(false);
      dispatch(removeActiveRequests("modify_strategy"));
      dispatch(updateLoading(false));
      dispatch(updateLoadingMsg(""));
    }
  };

  const pageBg =
    theme.palette.mode === "dark" ? theme.palette.grey[50] : "#ebebeb";

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "0.8125rem",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: "nowrap",
    py: 1.5,
  };

  const bodyCellSx = {
    borderBottom: `1px solid ${theme.palette.divider}`,
    py: 1.25,
    verticalAlign: "middle",
  };

  if (!strategy) {
    return (
      <>
        <Helmet>
          <title>Strategy Permissions | Vantage Alpha</title>
        </Helmet>
        <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 3 }}>
          <Container maxWidth="lg">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={goBackToStrategies}
              sx={{ textTransform: "none", mb: 2 }}
            >
              Back to Strategies
            </Button>
            <Alert severity="warning">
              Strategy not found. It may have been deleted or is not loaded yet.
            </Alert>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Strategy Permissions | Vantage Alpha</title>
      </Helmet>
      <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 3 }}>
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBackToStrategies}
            sx={{
              textTransform: "none",
              color: theme.palette.text.secondary,
              mb: 2,
            }}
          >
            Back to Strategies
          </Button>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
                <SecurityIcon />
              </Avatar>
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                >
                  Strategy Permissions
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {strategy.name || "Untitled strategy"}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!canEdit || saving}
              sx={{ textTransform: "none", fontWeight: 600, minWidth: 120 }}
            >
              Save
            </Button>
          </Box>

          {!canEdit && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You need to be the strategy owner or have Write access to edit
              permissions.
            </Alert>
          )}

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  Default access
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Applied when a user or manager has no specific permission.
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 180 }} disabled={!canEdit}>
                <InputLabel id="default-permission-label">Default</InputLabel>
                <Select
                  labelId="default-permission-label"
                  label="Default"
                  value={defaultLevel}
                  onChange={(e) => setDefaultLevel(Number(e.target.value))}
                >
                  {PERMISSION_LEVEL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ px: 1, overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 780 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellSx, minWidth: 320 }}>
                      User / Manager
                    </TableCell>
                    {LEVEL_COLUMNS.map((col) => (
                      <TableCell
                        key={col.value}
                        align="center"
                        sx={{ ...headerCellSx, minWidth: 100 }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ ...headerCellSx, width: 64 }}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const selectedManager =
                      (managers || []).find((m) => m.managerId === row.key) ||
                      (row.key
                        ? { managerId: row.key, name: managerLabel(row.key) }
                        : null);
                    const managerOptions = (() => {
                      const list = availableManagersForRow(row.id);
                      if (
                        selectedManager?.managerId &&
                        !list.some(
                          (m) => m.managerId === selectedManager.managerId
                        )
                      ) {
                        list.unshift(selectedManager);
                      }
                      return list;
                    })();

                    return (
                      <TableRow key={row.id} hover>
                        <TableCell sx={bodyCellSx}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              value={row.kind}
                              disabled={!canEdit}
                              onChange={(_, next) =>
                                handleKindChange(row.id, next)
                              }
                              sx={{
                                flexShrink: 0,
                                "& .MuiToggleButton-root": {
                                  textTransform: "none",
                                  px: 1.25,
                                  py: 0.5,
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                },
                              }}
                            >
                              <ToggleButton value="user">User</ToggleButton>
                              <ToggleButton value="manager">
                                Manager
                              </ToggleButton>
                            </ToggleButtonGroup>

                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{ mx: 0.25 }}
                            />

                            {row.kind === "user" ? (
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="email@example.com"
                                value={row.key}
                                disabled={!canEdit}
                                onChange={(e) =>
                                  updateRow(row.id, { key: e.target.value })
                                }
                              />
                            ) : (
                              <Autocomplete
                                size="small"
                                fullWidth
                                disabled={!canEdit}
                                options={managerOptions}
                                value={selectedManager}
                                getOptionLabel={(opt) =>
                                  opt?.name || opt?.managerId || ""
                                }
                                isOptionEqualToValue={(a, b) =>
                                  a?.managerId === b?.managerId
                                }
                                onChange={(_, value) =>
                                  updateRow(row.id, {
                                    key: value?.managerId || "",
                                  })
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select manager"
                                  />
                                )}
                                noOptionsText="No managers"
                              />
                            )}
                          </Box>
                        </TableCell>

                        {LEVEL_COLUMNS.map((col) => (
                          <TableCell
                            key={col.value}
                            align="center"
                            sx={bodyCellSx}
                          >
                            <Radio
                              checked={Number(row.level) === col.value}
                              disabled={!canEdit}
                              onChange={() =>
                                updateRow(row.id, { level: col.value })
                              }
                              value={col.value}
                              name={`perm-${row.id}`}
                              size="small"
                              inputProps={{ "aria-label": col.label }}
                            />
                          </TableCell>
                        ))}

                        <TableCell align="center" sx={bodyCellSx}>
                          <Tooltip title="Remove row">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={!canEdit}
                                onClick={() => handleDeleteRow(row.id)}
                                aria-label="Remove permission row"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Tooltip title="Add row">
                <span>
                  <IconButton
                    color="primary"
                    disabled={!canEdit}
                    onClick={handleAddRow}
                    aria-label="Add permission row"
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary }}
              >
                Add a row for each user or manager, then Save.
              </Typography>
            </Box>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default StrategyPermissionsPage;
